import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import DirectChatDrawer from '../components/DirectChatDrawer';
import {
  MessageSquare, Hash, ArrowLeft, ChevronRight, Loader2, Users, UserCircle2,
  Headphones, Wrench, ShieldCheck, Calculator, Building2, Briefcase,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPT_ICON = {
  dispatch: Headphones,
  admin: Building2,
  hr: Briefcase,
  fleet: Wrench,
  accounting: Calculator,
  safety: ShieldCheck,
};

const ROLE_LABEL = {
  super_admin: 'Admin',
  company_admin: 'Admin',
  dispatcher: 'Dispatcher',
  driver: 'Driver',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * TeamChat
 * Step 1: Inbox (your active threads) + "New chat" → Department picker
 * Step 2: Department detail → channel at top, members below
 * Step 3: Drawer opens with the selected thread
 */
const TeamChat = () => {
  const { user } = useAuth();
  const [view, setView] = useState('inbox');         // 'inbox' | 'departments' | 'department-detail'
  const [threads, setThreads] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptDetail, setDeptDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/direct-chat/threads`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch (err) { console.error('[TeamChat] loadInbox failed', err); } finally { setLoading(false); }
  }, []);

  const loadDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/direct-chat/departments`, { withCredentials: true });
      setDepartments(res.data.departments || []);
    } catch (err) { console.error('[TeamChat] loadDepartments failed', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'inbox') loadInbox();
    if (view === 'departments') loadDepartments();
  }, [view, loadInbox, loadDepartments]);

  const openDepartment = async (dept) => {
    setSelectedDept(dept);
    setView('department-detail');
    setLoading(true);
    try {
      const res = await axios.get(`${API}/direct-chat/departments/${dept.key}`, { withCredentials: true });
      setDeptDetail(res.data);
    } catch (err) { console.error('[TeamChat] openDepartment detail failed', err); } finally { setLoading(false); }
  };

  const openOrCreateThread = async (payload) => {
    try {
      const res = await axios.post(`${API}/direct-chat/threads`, payload, { withCredentials: true });
      const t = res.data;
      // Augment with display info (channel needs label, DM needs other_user)
      if (t.kind === 'channel') {
        t.department_label = selectedDept?.label || t.department_label;
      } else if (t.kind === 'dm') {
        // Find recipient in deptDetail.members for display
        const member = deptDetail?.members?.find((m) => m.id === payload.recipient_id);
        if (member) t.other_user = member;
      }
      setActiveThread(t);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to open chat');
    }
  };

  const openThreadFromInbox = async (t) => {
    // Inbox thread already has the augmentation
    setActiveThread(t);
  };

  const closeDrawerAndRefresh = () => {
    setActiveThread(null);
    if (view === 'inbox') loadInbox();
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="team-chat-page">
        <Header view={view} onBack={() => {
          if (view === 'department-detail') setView('departments');
          else if (view === 'departments') setView('inbox');
        }} onNewChat={() => setView('departments')} selectedDept={selectedDept} user={user} />

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : view === 'inbox' ? (
          <InboxList threads={threads} onOpen={openThreadFromInbox} onNewChat={() => setView('departments')} />
        ) : view === 'departments' ? (
          <DepartmentGrid departments={departments} onPick={openDepartment} />
        ) : (
          <DepartmentDetail
            dept={selectedDept}
            detail={deptDetail}
            onChannel={() => openOrCreateThread({ kind: 'channel', department: selectedDept.key })}
            onMember={(memberId) => openOrCreateThread({ kind: 'dm', recipient_id: memberId })}
          />
        )}
      </div>

      <DirectChatDrawer
        thread={activeThread}
        open={!!activeThread}
        onClose={closeDrawerAndRefresh}
      />
    </DashboardLayout>
  );
};

const Header = ({ view, onBack, onNewChat, selectedDept }) => (
  <div className="mb-6">
    {view !== 'inbox' && (
      <button
        data-testid="team-chat-back"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#002FA7] mb-2"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
    )}
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          {view === 'inbox' && 'Team Messages'}
          {view === 'departments' && 'Start a new chat'}
          {view === 'department-detail' && (selectedDept?.label || 'Department')}
        </h1>
        <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {view === 'inbox' && 'Chat with members of your organization.'}
          {view === 'departments' && 'Pick a department to message.'}
          {view === 'department-detail' && 'Channel + 1-on-1 conversations.'}
        </p>
      </div>
      {view === 'inbox' && (
        <button
          onClick={onNewChat}
          data-testid="team-chat-new"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold text-sm hover:bg-[#00247D] transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <MessageSquare className="w-4 h-4" /> New chat
        </button>
      )}
    </div>
  </div>
);

const InboxList = ({ threads, onOpen, onNewChat }) => {
  if (!threads.length) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="team-chat-empty">
        <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No team conversations yet.</p>
        <button
          onClick={onNewChat}
          data-testid="team-chat-empty-start"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold text-sm hover:bg-[#00247D]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <MessageSquare className="w-4 h-4" /> Start a chat
        </button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="team-chat-inbox">
      {threads.map((t) => {
        const isChan = t.kind === 'channel';
        const title = isChan
          ? `#${t.department_label || t.department}`
          : (t.other_user?.name || 'Direct message');
        const subtitle = isChan
          ? 'Department channel'
          : (ROLE_LABEL[t.other_user?.role] || t.other_user?.role || 'Member');
        return (
          <button
            key={t.id}
            data-testid={`team-chat-thread-${t.id}`}
            onClick={() => onOpen(t)}
            className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-[#002FA7]/10 rounded-md flex items-center justify-center flex-shrink-0">
              {isChan ? <Hash className="w-4 h-4 text-[#002FA7]" /> : <UserCircle2 className="w-4 h-4 text-[#002FA7]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</p>
                {t.unread > 0 && (
                  <span className="bg-[#FF3B30] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center" data-testid={`team-chat-unread-${t.id}`}>
                    {t.unread}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t.last_message_preview || subtitle}
              </p>
            </div>
            <span className="text-[10px] text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{formatRelative(t.last_message_at)}</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
          </button>
        );
      })}
    </div>
  );
};

const DepartmentGrid = ({ departments, onPick }) => {
  if (!departments.length) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="team-chat-no-depts">
        <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          No departments have been configured yet. Ask your admin to enable them under Settings.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="team-chat-departments">
      {departments.map((d) => {
        const Icon = DEPT_ICON[d.key] || Users;
        return (
          <button
            key={d.key}
            data-testid={`team-chat-dept-${d.key}`}
            onClick={() => onPick(d)}
            className="bg-white border border-slate-200 rounded-md p-4 hover:border-[#002FA7] hover:shadow-sm transition-all text-left flex items-center gap-3"
          >
            <div className="w-11 h-11 bg-[#002FA7]/10 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-[#002FA7]" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{d.label}</p>
              <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {d.member_count} {d.member_count === 1 ? 'member' : 'members'}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </button>
        );
      })}
    </div>
  );
};

const DepartmentDetail = ({ dept, detail, onChannel, onMember }) => {
  if (!detail) return null;
  const Icon = DEPT_ICON[dept.key] || Users;
  return (
    <div className="space-y-5" data-testid="team-chat-dept-detail">
      <div className="bg-white border border-slate-200 rounded-md">
        <button
          onClick={onChannel}
          data-testid="team-chat-channel-button"
          className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
        >
          <div className="w-11 h-11 bg-[#002FA7] rounded-md flex items-center justify-center flex-shrink-0">
            <Hash className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              #{dept.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
            </p>
            <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Broadcast to the whole {dept.label} department
            </p>
          </div>
          <span className="text-xs font-semibold text-[#002FA7] flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Open <ChevronRight className="w-3 h-3" />
          </span>
        </button>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <Icon className="w-3.5 h-3.5" /> Direct message a {dept.label} member
        </h3>
        {detail.members.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-6 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            No members to DM in this department yet.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {detail.members.map((m) => (
              <button
                key={m.id}
                data-testid={`team-chat-member-${m.id}`}
                onClick={() => onMember(m.id)}
                className="w-full text-left p-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
              >
                <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 font-semibold uppercase">
                  {(m.name || m.email || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{m.name}</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {ROLE_LABEL[m.role] || m.role}
                  </p>
                </div>
                <MessageSquare className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamChat;
